import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateKanbanPagesOrderAction } from '../actions/updateKanbanPagesOrder.action';
import { KanbanPage } from '../interfaces/kanbanPage.interface';

export const useUpdateKanbanPagesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pageOrders: { pageId: string; order: number }[]) =>
      updateKanbanPagesOrderAction(pageOrders),
    onMutate: async (pageOrders) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['kanban-pages'] });

      // Snapshot the previous value
      const previousPages = queryClient.getQueryData<KanbanPage[]>(['kanban-pages']);

      // Optimistically update the order
      if (previousPages) {
        const updatedPages = [...previousPages];
        pageOrders.forEach(({ pageId, order }) => {
          const pageIndex = updatedPages.findIndex((p) => p.uid === pageId);
          if (pageIndex !== -1) {
            updatedPages[pageIndex] = {
              ...updatedPages[pageIndex],
              order,
            };
          }
        });
        // Sort by order
        updatedPages.sort((a, b) => (a.order || 0) - (b.order || 0));
        queryClient.setQueryData<KanbanPage[]>(['kanban-pages'], updatedPages);
      }

      return { previousPages };
    },
    onError: (err, pageOrders, context) => {
      // Rollback optimistic update
      if (context?.previousPages) {
        queryClient.setQueryData(['kanban-pages'], context.previousPages);
      }
    },
    onSettled: () => {
      // Invalidate to refetch
      queryClient.invalidateQueries({ queryKey: ['kanban-pages'] });
    },
  });
};

